import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck,
  ChevronRight,
  Trash2,
  ExternalLink,
  MessageSquare,
  Video,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react';
import type { SessionId, VisitorId } from '@prakash-software/shared';
import { apiFetch, isApiError } from '../lib/api';
import {
  deleteContact,
  deleteContactSession,
  listContactSessions,
  listIdentifiedVisitors,
  mergeContacts,
  unlinkIdentity,
  type ContactIdentity,
  type ContactSession,
  type IdentifiedVisitor,
} from '../lib/identified-visitors-api';
import { formatDateTime, formatRelativeOrAbsolute } from '../lib/datetime';
import { useAgentStore } from '../stores/agent';
import { useConversationsStore } from '../stores/conversations';
import { useNotificationsStore } from '../stores/notifications';
import { useVisitorsStore } from '../stores/visitors';
import {
  Button,
  Badge,
  Skeleton,
  Avatar,
  AvatarFallback,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui';
import { ContactTimeline } from '../components/ContactTimeline';
import { ContactAttributes } from '../components/ContactAttributes';
import styles from './Analytics.module.css';
import own from './Contacts.module.css';
import { confirmDialog } from '../stores/confirm';

interface SiteLite {
  id: string;
  name: string;
  slug: string;
}

const fmtWhen = (iso: string | null): string => (iso ? formatDateTime(Date.parse(iso)) : '—');
const fmtAgo = (iso: string | null): string =>
  iso ? formatRelativeOrAbsolute(Date.parse(iso)) : '—';

function location(v: IdentifiedVisitor): string {
  return [v.city, v.country].filter(Boolean).join(', ') || '—';
}

const CHANNEL_LABEL: Record<string, string> = { web: 'Web', whatsapp: 'WhatsApp', api: 'API', instagram: 'Instagram', messenger: 'Messenger' };

function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

function contactLabel(v: IdentifiedVisitor): string {
  return v.name || v.email || v.phone || 'this contact';
}

function toast(title: string): void {
  useNotificationsStore.getState().push({ kind: 'system', title });
}

function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0][0] || '') + (parts[1][0] || '')).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// A "View chats" button for a contact. Prefer opening Chat history filtered by
// the contact's email/phone (shows ALL their conversations); fall back to their
// latest conversation if we have no identifier to search by.
function conversationsLink(v: IdentifiedVisitor) {
  const term = v.email || v.phone || v.name;
  if (term) {
    return (
      <Link
        to={`/dashboard?mode=history&q=${encodeURIComponent(term)}`}
        title="View this contact’s conversations"
      >
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
          <span>View chats</span>
          <ChevronRight size={12} />
        </Button>
      </Link>
    );
  }
  if (v.lastSessionId) {
    return (
      <Link
        to={`/conversation/${v.lastSessionId}`}
        title="Open this contact’s conversation"
      >
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
          <span>View chat</span>
          <ChevronRight size={12} />
        </Button>
      </Link>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

// Per-site list of visitors who shared contact details. Same access model as
// Analytics (super-admin + site-admin); the server authorizes each site.
export function Contacts() {
  const [sites, setSites] = useState<SiteLite[]>([]);
  const [siteId, setSiteId] = useState('');
  const [rows, setRows] = useState<IdentifiedVisitor[]>([]);
  const [capped, setCapped] = useState(false);
  const [scope, setScope] = useState<'all' | 'mine'>('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which contact's chat list is open, and that list once fetched.
  const [openId, setOpenId] = useState<string | null>(null);
  const [chats, setChats] = useState<ContactSession[] | null>(null);
  const [chatsError, setChatsError] = useState<string | null>(null);
  // visitorId or `${visitorId}:${sessionId}` while its delete is in flight.
  const [busy, setBusy] = useState<string | null>(null);
  // Which contact's activity timeline is open.
  const [activityId, setActivityId] = useState<string | null>(null);
  // Rows ticked for a merge (at most two), and a bump to reload after one.
  const [selected, setSelected] = useState<string[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const role = useAgentStore((s) => s.current?.role);
  // Purge rights mirror the server: super-admin anywhere, otherwise an admin of
  // this site. `scope === 'all'` is the server telling us it treated us as a
  // site admin for the listing. Server re-checks on every call regardless.
  const canPurge =
    role === 'super-admin' || role === 'account-admin' || role === 'site-admin' || scope === 'all';

  useEffect(() => {
    apiFetch<{ sites: SiteLite[] }>('/api/admin/sites')
      .then((r) => {
        setSites(r.sites);
        setSiteId((prev) => prev || r.sites[0]?.id || '');
        if (r.sites.length === 0) setLoading(false);
      })
      .catch(() => {
        setError('Couldn’t load sites.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!siteId) return;
    let ignore = false;
    setLoading(true);
    setError(null);
    setOpenId(null);
    setChats(null);
    setSelected([]);
    listIdentifiedVisitors(siteId)
      .then((r) => {
        if (ignore) return;
        setRows(r.visitors);
        setCapped(r.capped);
        setScope(r.scope);
      })
      .catch(() => {
        if (!ignore) setError('Couldn’t load visitors for this site.');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [siteId, reloadKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((v) =>
      [v.name, v.email, v.phone, v.city, v.country, v.region]
        .filter(Boolean)
        .some((f) => f!.toLowerCase().includes(q)),
    );
  }, [rows, query]);

  const toggleChats = useCallback((visitorId: string) => {
    setChatsError(null);
    setOpenId((prev) => {
      if (prev === visitorId) return null;
      setChats(null);
      listContactSessions(visitorId)
        .then((list) => setChats(list))
        .catch(() => setChatsError('Couldn’t load this contact’s chats.'));
      return visitorId;
    });
  }, []);

  async function onDeleteChat(v: IdentifiedVisitor, chat: ContactSession) {
    const when = fmtWhen(chat.startedAt);
    const ok = await confirmDialog({
      title: `Permanently delete the chat from ${when} with ${contactLabel(v)}?`,
      message:
        `${chat.messageCount} message${chat.messageCount === 1 ? '' : 's'}, plus its ` +
        `attachments, notes, transcripts and recordings, will be erased. This cannot be undone.`,
    });
    if (!ok) return;
    const key = `${v.visitorId}:${chat.sessionId}`;
    setBusy(key);
    try {
      await deleteContactSession(chat.visitorId ?? v.visitorId, chat.sessionId);
      setChats((prev) => prev?.filter((c) => c.sessionId !== chat.sessionId) ?? prev);
      // Drop it from the open conversation stack too, so the agent isn't left
      // looking at a thread whose rows no longer exist.
      useConversationsStore.getState().close(chat.sessionId as SessionId);
      toast('Chat deleted');
    } catch (err) {
      toast(deleteErrorMessage(err, 'chat'));
    } finally {
      setBusy(null);
    }
  }

  function toggleSelected(visitorId: string) {
    setSelected((prev) =>
      prev.includes(visitorId) ? prev.filter((id) => id !== visitorId) : [...prev, visitorId].slice(-2),
    );
  }

  // Merge the two ticked people. The one seen most recently is kept, so their
  // row (and latest conversation) is what remains.
  async function onMerge() {
    const pair = rows.filter((r) => selected.includes(r.visitorId));
    if (pair.length !== 2) return;
    const [keep, merge] = [...pair].sort((a, b) => Date.parse(b.lastSeenAt ?? '') - Date.parse(a.lastSeenAt ?? '')) as [
      IdentifiedVisitor,
      IdentifiedVisitor,
    ];
    const ok = await confirmDialog({
      title: `Merge ${contactLabel(merge)} into ${contactLabel(keep)}?`,
      tone: 'default',
      confirmLabel: 'Merge',
      message:
        `They become one person: every identity, chat and ticket of ${contactLabel(merge)} moves to ` +
        `${contactLabel(keep)}, who keeps their own details and gains any they were missing. ` +
        `An identity merged by mistake can be unlinked again from Activity.`,
    });
    if (!ok) return;
    setBusy('merge');
    try {
      await mergeContacts(siteId, keep.visitorId, merge.visitorId);
      toast(`Merged into ${contactLabel(keep)}`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast(
        isApiError(err) && err.status === 409
          ? 'Those two are already the same person.'
          : isApiError(err) && err.status === 403
            ? 'Only a site admin can merge contacts.'
            : 'Couldn’t merge these contacts. Try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  async function onUnlink(v: IdentifiedVisitor, identity: ContactIdentity) {
    const ok = await confirmDialog({
      title: `Unlink this ${channelLabel(identity.channel)} identity from ${contactLabel(v)}?`,
      message: 'It becomes a separate contact, taking its chats and the tickets it raised.',
      tone: 'default',
      confirmLabel: 'Unlink',
    });
    if (!ok) return;
    setBusy(`unlink:${identity.visitorId}`);
    try {
      await unlinkIdentity(siteId, identity.visitorId);
      toast('Identity unlinked');
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast(
        isApiError(err) && err.status === 409
          ? 'That is this contact’s only identity.'
          : 'Couldn’t unlink this identity. Try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  async function onDeleteContact(v: IdentifiedVisitor) {
    // Fetch the footprint first so the confirm can state what actually goes —
    // "and 6 chats" is a very different decision from "and 0 chats".
    let count: number | null = null;
    try {
      count =
        openId === v.visitorId && chats
          ? chats.length
          : (await listContactSessions(v.visitorId)).length;
    } catch {
      /* fall back to an unquantified warning rather than blocking the delete */
    }
    const chatLine =
      count === null ? 'Every chat they had' : `${count} chat${count === 1 ? '' : 's'}`;
    const ok = await confirmDialog({
      title: `Permanently delete ${contactLabel(v)}?`,
      message:
        `${chatLine}, with all messages, attachments, notes, transcripts, recordings ` +
        `and their page history, will be erased along with the contact. This cannot be undone.`,
    });
    if (!ok) return;
    setBusy(v.visitorId);
    try {
      const counts = await deleteContact(v.visitorId);
      setRows((prev) => prev.filter((r) => r.visitorId !== v.visitorId));
      if (openId === v.visitorId) {
        setOpenId(null);
        setChats(null);
      }
      // Deleting a person erases every identity linked to them.
      for (const id of new Set([v.visitorId, ...v.identities.map((i) => i.visitorId)])) {
        useVisitorsStore.getState().remove(id as VisitorId);
      }
      toast(
        counts.sessions > 0
          ? `Contact deleted · ${counts.sessions} chat${counts.sessions === 1 ? '' : 's'} removed`
          : 'Contact deleted',
      );
    } catch (err) {
      toast(deleteErrorMessage(err, 'contact'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <div className={styles.kickerBadge}>
            <span className={styles.kickerDot} />
            <span className={styles.kickerText}>
              {scope === 'mine' ? 'CONTACTS · HANDLED CHATS' : 'DIRECTORY · IDENTIFIED VISITORS'}
            </span>
          </div>
          <h1 className={styles.title}>Identified visitors</h1>
        </div>
        <div className={styles.controls}>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Site</span>
            <select
              className={styles.select}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.control}>
            <span className={styles.controlLabel}>Search</span>
            <input
              className={styles.search}
              type="search"
              placeholder="Name, email, phone, location…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </div>
      </header>

      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {!error && sites.length === 0 ? (
        <p className={styles.empty}>No sites are assigned to you.</p>
      ) : null}

      {!loading && !error && sites.length > 0 ? (
        <div className={styles.panel}>
          <div className="flex items-center justify-between pb-3 border-b border-border mb-3">
            <p className={styles.panelTitle}>
              {filtered.length} {filtered.length === 1 ? 'contact' : 'contacts'}
              {query ? ` matching “${query}”` : ''}
              {capped ? ' · showing the latest 500' : ''}
            </p>
            <span className="text-xs text-muted-foreground">Directory Intelligence</span>
          </div>
          {canPurge ? (
            <div className={own.mergeBar}>
              <span className={styles.muted}>
                {selected.length === 0
                  ? 'Tick two contacts that are the same person to merge them.'
                  : `${selected.length} of 2 selected`}
              </span>
              <button
                type="button"
                className={own.expandBtn}
                disabled={selected.length !== 2 || busy === 'merge'}
                onClick={() => void onMerge()}
              >
                {busy === 'merge' ? 'Merging…' : 'Merge selected'}
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-xl my-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-3.5">
                <UserCheck size={26} />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">
                {rows.length === 0 ? 'No contacts identified yet' : 'No contacts match your search'}
              </h3>
              <p className="text-xs text-muted-foreground max-w-xs mb-4">
                {rows.length === 0
                  ? 'Visitors who share their email or phone number in chat will appear here automatically.'
                  : 'Try searching by a different name, email domain, or city.'}
              </p>
              {query && (
                <Button variant="outline" size="sm" onClick={() => setQuery('')}>
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.tableScroll}>
              <Table>
                <TableHeader>
                  <TableRow>
                    {canPurge ? <TableHead className="w-[40px]" aria-label="Select" /> : null}
                    <TableHead>Contact</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Identified</TableHead>
                    <TableHead>Last seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((v) => (
                    <FragmentRow
                      key={v.visitorId}
                      v={v}
                      siteId={siteId}
                      open={openId === v.visitorId}
                      chats={openId === v.visitorId ? chats : null}
                      chatsError={openId === v.visitorId ? chatsError : null}
                      canPurge={canPurge}
                      busy={busy}
                      onToggle={() => toggleChats(v.visitorId)}
                      activityOpen={activityId === v.visitorId}
                      onToggleActivity={() => setActivityId((id) => (id === v.visitorId ? null : v.visitorId))}
                      onDeleteChat={(c) => void onDeleteChat(v, c)}
                      onDeleteContact={() => void onDeleteContact(v)}
                      selected={selected.includes(v.visitorId)}
                      onSelect={() => toggleSelected(v.visitorId)}
                      onUnlink={(identity) => void onUnlink(v, identity)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

interface RowProps {
  v: IdentifiedVisitor;
  siteId: string;
  open: boolean;
  chats: ContactSession[] | null;
  chatsError: string | null;
  canPurge: boolean;
  busy: string | null;
  onToggle: () => void;
  activityOpen: boolean;
  onToggleActivity: () => void;
  onDeleteChat: (chat: ContactSession) => void;
  onDeleteContact: () => void;
  selected: boolean;
  onSelect: () => void;
  onUnlink: (identity: ContactIdentity) => void;
}

function FragmentRow({
  v,
  open,
  chats,
  chatsError,
  canPurge,
  busy,
  onToggle,
  activityOpen,
  onToggleActivity,
  onDeleteChat,
  onDeleteContact,
  selected,
  onSelect,
  onUnlink,
  siteId,
}: RowProps) {
  const contactBusy = busy === v.visitorId;
  const initials = getInitials(v.name || v.email);
  const span = canPurge ? 8 : 7;

  return (
    <>
      <TableRow className="hover:bg-muted/40 transition-colors">
        {canPurge ? (
          <TableCell>
            <input
              type="checkbox"
              checked={selected}
              onChange={onSelect}
              aria-label={`Select ${contactLabel(v)} to merge`}
            />
          </TableCell>
        ) : null}
        <TableCell>
          <div className="flex items-center gap-2.5">
            <Avatar className="h-8 w-8 text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-foreground text-xs flex items-center gap-1.5">
                <span>{v.name || <span className="text-muted-foreground italic">Anonymous</span>}</span>
                {v.isReturning && (
                  <Badge variant="outline" className="text-[9px] uppercase px-1.5 py-0 text-blue-500 border-blue-500/20 bg-blue-500/10">
                    Returning
                  </Badge>
                )}
                {v.channels.length > 1 || v.channels[0] !== 'web'
                  ? v.channels.map((c) => (
                      <span key={c} className={own.channel} title={`Has contacted you via ${channelLabel(c)}`}>
                        {channelLabel(c)}
                      </span>
                    ))
                  : null}
              </div>
              <div className="text-[11px] text-muted-foreground font-mono">
                {v.visitorId.slice(0, 8)}…
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell className="font-mono text-xs">
          {v.email ? (
            <a href={`mailto:${v.email}`} className="text-primary hover:underline flex items-center gap-1">
              <Mail size={11} className="text-muted-foreground" />
              <span>{v.email}</span>
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="font-mono text-xs">
          {v.phone ? (
            <a href={`tel:${v.phone}`} className="text-foreground hover:underline flex items-center gap-1">
              <Phone size={11} className="text-muted-foreground" />
              <span>{v.phone}</span>
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {v.city || v.country ? (
            <span className="flex items-center gap-1">
              <MapPin size={11} className="text-muted-foreground" />
              <span>{location(v)}</span>
            </span>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground" title={fmtWhen(v.identifiedAt)}>
          {fmtAgo(v.identifiedAt)}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground" title={fmtWhen(v.lastSeenAt)}>
          {fmtAgo(v.lastSeenAt)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1.5">
            {conversationsLink(v)}
            <button
              type="button"
              className={own.expandBtn}
              onClick={onToggleActivity}
              aria-expanded={activityOpen}
              title="Everything this contact did, across all channels"
            >
              <span className={`${own.chevron} ${activityOpen ? own.chevronOpen : ''}`}>›</span>
              Activity
            </button>
            {canPurge ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={onToggle}
                  aria-expanded={open}
                  title="Show contact chat history"
                >
                  <ChevronRight size={12} className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
                  <span>Chats</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive gap-1 px-2"
                  onClick={onDeleteContact}
                  disabled={contactBusy}
                  title="Permanently purge this contact and their data"
                >
                  <Trash2 size={12} />
                  <span>{contactBusy ? '…' : 'Purge'}</span>
                </Button>
              </>
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {open ? (
        <TableRow>
          <TableCell colSpan={span} className="bg-muted/30 p-4 border-b border-border">
            {chatsError ? <p className="text-xs text-destructive">{chatsError}</p> : null}
            {!chatsError && chats === null ? (
              <div className="space-y-2 py-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : null}
            {chats?.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">This contact has no archived chat sessions.</p>
            ) : null}
            {chats && chats.length > 0 ? (
              <ul className={own.chatsList}>
                {chats.map((c) => {
                  const rowBusy = busy === `${v.visitorId}:${c.sessionId}`;
                  return (
                    <li key={c.sessionId} className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-background border border-border text-xs mb-1.5 last:mb-0">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-muted-foreground text-[11px]" title={fmtWhen(c.startedAt)}>
                          {fmtAgo(c.startedAt)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {c.medium === 'video' ? (
                            <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-400/30 gap-1">
                              <Video size={10} /> Video
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-400/30 gap-1">
                              <MessageSquare size={10} /> Chat
                            </Badge>
                          )}
                          {c.channel === 'whatsapp' ? (
                            <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-400/30">
                              WhatsApp
                            </Badge>
                          ) : c.channel === 'instagram' ? (
                            <Badge variant="outline" className="text-[10px] text-pink-400 border-pink-400/30">
                              Instagram
                            </Badge>
                          ) : c.channel === 'messenger' ? (
                            <Badge variant="outline" className="text-[10px] text-blue-400 border-blue-400/30">
                              Messenger
                            </Badge>
                          ) : c.channel === 'email' ? (
                            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-400/30">
                              Email
                            </Badge>
                          ) : c.channel === 'api' ? (
                            <Badge variant="outline" className="text-[10px] text-sky-400 border-sky-400/30">
                              API
                            </Badge>
                          ) : null}
                          <span className="text-foreground">
                            {c.messageCount} {c.messageCount === 1 ? 'message' : 'messages'}
                          </span>
                          {c.agentName && <span className="text-muted-foreground">· handled by {c.agentName}</span>}
                          {c.starred && <span className="text-amber-400">★</span>}
                          {c.deletedAt && <Badge variant="destructive" className="text-[9px]">Hidden</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/conversation/${c.sessionId}`}>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                            <span>Open</span>
                            <ExternalLink size={11} />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:bg-destructive/10 px-2"
                          onClick={() => onDeleteChat(c)}
                          disabled={rowBusy}
                          title="Permanently delete this chat"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </TableCell>
        </TableRow>
      ) : null}
      {activityOpen ? (
        <tr>
          <td className={own.chatsCell} colSpan={span}>
            {v.identities.length > 1 ? (
              <div className={own.identities}>
                <p className={own.identitiesTitle}>Linked identities</p>
                <ul className={own.chatsList}>
                  {v.identities.map((i) => (
                    <li key={i.visitorId} className={own.chatRow}>
                      <span className={own.channel}>{channelLabel(i.channel)}</span>
                      <span className={own.chatMeta}>
                        {[i.name, i.email, i.phone].filter(Boolean).join(' · ') || 'No details'}
                      </span>
                      <span className={own.chatWhen} title={fmtWhen(i.lastSeenAt)}>
                        {fmtAgo(i.lastSeenAt)}
                      </span>
                      {canPurge ? (
                        <span className={own.chatActions}>
                          <button
                            type="button"
                            className={own.expandBtn}
                            disabled={busy === `unlink:${i.visitorId}`}
                            onClick={() => onUnlink(i)}
                            title="Split this identity into a contact of its own"
                          >
                            Unlink
                          </button>
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {v.contactId ? <ContactAttributes siteId={siteId} contactId={v.contactId} /> : null}
            <ContactTimeline visitorId={v.visitorId} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function deleteErrorMessage(err: unknown, what: 'chat' | 'contact'): string {
  if (isApiError(err) && err.status === 403) {
    return `Not allowed — only a site admin can delete a ${what}.`;
  }
  if (isApiError(err) && err.status === 404) {
    return `That ${what} no longer exists.`;
  }
  return `Couldn’t delete the ${what}. Try again.`;
}

